import { useAuthStore } from '@/v2/store/shared/auth-store';
import { t } from '../../../../locales';
import { Alert } from '../../../plugins/alert';
import { api } from '../../../plugins/axios';

export class Marketplace {
	id = undefined;
	name = undefined;
	store_name = undefined;

	constructor(obj) {
		Object.keys(obj).forEach((key) => {
			this[key] = obj[key];
		});
	}

	get displayName() {
		let name = this.name;

		if (this.store_name) {
			name += ` (${this.store_name})`;
		}

		return name;
	}

	static async getLinkedMarketplaces(params) {
		const { data, totalRecords } = await api.get('/marketplace/list', { params }).then((res) => ({
			data: res.data.result.marketplaces,
			totalRecords: res.data.result.marketplaces.length,
		}));
		return {
			data: data.map((item) => new Marketplace(item)),
			totalRecords,
		};
	}

	static async getUnlinkedMarketplaces() {
		return api.get('/marketplace/ajaxMarketplaces').then((res) => res.data.results);
	}

	static async createMarketplace(data) {
		return api.post('/marketplace/store', data);
	}

	static async createCustomMarketplace(data) {
		return api.post('/marketplace/store-custom-app', data).then(({ data }) => data);
	}

	static async getWizard(linked_marketplace_id) {
		return api
			.get(`/tenant/wizard?filters[linked_marketplace_id]=${linked_marketplace_id}`)
			.then((res) => {
				return {
					steps: res.data.result.LinkedMarketplaceWizards,
					whatsappQrSession: res.data.result.whatsapp_qr_session,
				};
			});
	}

	static async finishWizardStep(stepId) {
		return api.get(`/tenant/finish/${stepId}`);
	}

	static async authorizeStore(linked_marketplace_id, params) {
		try {
			const data = await api
				.get(`/marketplace/authorize/${linked_marketplace_id}`, { params })
				.then((res) => res.data);
			if (data.success) {
				return { redirectUrl: data.redirect_url, success: true };
			} else {
				const msg = data?.message;
				Alert.error(msg || t('Error Msg'));
				return { success: false };
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async importProducts(linked_marketplace_id) {
		return api.get(`/marketplace/import_products/${linked_marketplace_id}`);
	}

	static async exportProducts(linked_marketplace_id) {
		return api.get(`/marketplace/export_products/${linked_marketplace_id}`);
	}

	static async importOrders(linked_marketplace_id) {
		const res = await api.get(`/marketplace/importOrders/${linked_marketplace_id}`);
		if (!res.data.success) {
			const msg = res.data.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw new Error(res.data.message);
		}
		return res;
	}

	static async importCategories(linked_marketplace_id) {
		return api.get(`/marketplace/import_category/${linked_marketplace_id}`);
	}

	static async exportCategories(linked_marketplace_id) {
		return api.get(`/marketplace/exportCategory/${linked_marketplace_id}`);
	}

	static async importOptions(linked_marketplace_id) {
		return api.get(`/marketplace/import_option/${linked_marketplace_id}`);
	}

	static async unlinkMarketplace(linked_marketplace_id) {
		return api.get(`/marketplace/unlinkMarketplace/${linked_marketplace_id}`);
	}

	static async deleteMarketplace(linked_marketplace_id) {
		return api.delete(`/marketplace/deleteMarketplace/${linked_marketplace_id}`);
	}

	static async createWhatsappCatalog(linked_marketplace_id) {
		return api.get(`/marketplace/create-whatsapp-catalog/${linked_marketplace_id}`);
	}

	static async disableWhatsappCart(linked_marketplace_id) {
		return api.get(`/marketplace/disable-cart-setting/${linked_marketplace_id}`);
	}

	static async enableWhatsappCart(linked_marketplace_id) {
		return api.get(`/marketplace/enable-cart-setting/${linked_marketplace_id}`);
	}

	static async toggleWelcomeMessage(linked_marketplace_id) {
		return api.get(`/marketplace/enable-welcome-message/${linked_marketplace_id}`);
	}

	static async enableUtilityTemplate(linked_marketplace_id) {
		return api.get(`/marketplace/enable-utility-template/${linked_marketplace_id}`);
	}

	static async enableMarketingTemplate(linked_marketplace_id) {
		return api.get(`/marketplace/enable-marketing-template/${linked_marketplace_id}`);
	}

	static async refreshToken(linked_marketplace_id) {
		return api.get(`/marketplace/refresh-token/${linked_marketplace_id}`);
	}

	static async createShopifyApp(linked_marketplace_id) {
		try {
			const res = await api
				.get(`/marketplace/create-shopify-app/${linked_marketplace_id}`)
				.then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async setNoonDetails(linked_marketplace_id, data) {
		try {
			const res = await api
				.post(`/marketplace/store-noon-details/${linked_marketplace_id}`, data)
				.then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async getNoonSftpDetails(linked_marketplace_id) {
		return api
			.get(`/marketplace/get-noon-sftp-details/${linked_marketplace_id}`)
			.then((res) => res.data.SftpDetails);
	}

	static async importNoonFiles(data) {
		try {
			await api.post(`/marketplace/import-noon-files`, data);
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async setTrendyolDetails(data, linkedMarketplaceId) {
		try {
			const url = linkedMarketplaceId ? `/trendyol/auth/save/${linkedMarketplaceId}` : `/trendyol/auth/save`;
			const res = await api.post(url, data).then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async saveWhatsAppManualToken(data) {
		try {
			const res = await api.post(`/marketplace/save-manual-token`, data).then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async saveInstagramManualToken(data) {
		try {
			const res = await api.post(`/v1/instagram/save-manual-auth`, data).then((res) => res.data);
			if (res.success) {
				const authStore = useAuthStore();
				await authStore.fetchUser(true, true);
				Alert.success(res.message);
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async saveMessengerManualToken(data) {
		try {
			const res = await api.post(`/v1/messenger/save-manual-auth`, data).then((res) => res.data);
			if (res.success) {
				const authStore = useAuthStore();
				await authStore.fetchUser(true, true);
				Alert.success(res.message || t('integrations.messenger-connected-successfully'));
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async getMessengerStatus() {
		try {
			const res = await api.get(`/v1/messenger/status`).then((res) => res.data);
			return res.result || res.data || res;
		} catch (err) {
			console.error(err);
			return { connected: false };
		}
	}

	static async disconnectMessenger() {
		try {
			const res = await api.delete(`/v1/messenger/disconnect`).then((res) => res.data);
			if (res.success) {
				const authStore = useAuthStore();
				await authStore.fetchUser(true, true);
				Alert.success(res.message);
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async manualSignUpCheck(data) {
		try {
			const res = await api
				.post(`/marketplace/manual-signup-check`, data)
				.then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async saveWhatsAppEmbeddedToken(linkedMarketPlace, data) {
		try {
			const res = await api
				.post(`/marketplace/save-embedded-token/${linkedMarketPlace}`, data)
				.then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async activateCustomApp(linked_marketplace_id) {
		return api.post(`/marketplace/activate-custom-app/${linked_marketplace_id}`);
	}

	static async setOdooDetails(data) {
		try {
			const res = await api.post(`/odoo/auth/save-credentials`, data).then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async setOdooConfig(data) {
		try {
			const res = await api.post(`/odoo/auth/save-config`, data).then((res) => res.data);
			if (res.success) {
				return true;
			} else {
				const msg = res?.message;
				Alert.error(msg || t('Error Msg'));
				return false;
			}
		} catch (err) {
			const msg = err?.response?.data?.message;
			Alert.error(msg || t('Error Msg'));
			console.error(err);
			throw err;
		}
	}

	static async startWhatsAppQRAuthentication(data) {
		return api.post(`/v1/whatsapp-qr/start-authentication`, data);
	}

	static async startWhatsAppQRSession(data) {
		return api.post(`/v1/whatsapp-qr/start-session`, data);
	}

	static async getWhatsAppQRCode(data) {
		return api.post(`/v1/whatsapp-qr/get-qr`, data);
	}
}

export const activeStatusEnum = Object.freeze({
	Complete: 'compleate',
	Active: 'ACTIVE',
	Disabled: 'DISABLED',
	NotComplete: 'not_compleate',
});

export const activeStatusBadgeColor = Object.freeze({
	[activeStatusEnum.Complete]: 'blue',
	[activeStatusEnum.Active]: 'success',
	[activeStatusEnum.Disabled]: 'error',
	[activeStatusEnum.NotComplete]: 'gray',
});
